/**
 * Vue directives for declarative analytics control.
 *
 * v-track-ignore  — opt an element and its subtree out of auto-tracking
 * v-track         — override the tracked event name and/or inject extra properties
 *
 * Both directives write data attributes that the global click listener reads,
 * so no Vue component lifecycle coupling is needed.
 */

import type { ObjectDirective } from 'vue'
import { AnalyticsEvents } from '../types'
import type { AnalyticsEventProperties } from '../types/properties'

/**
 * Shape of the v-track binding value.
 * `event` overrides the default AnalyticsEvents.ELEMENT_CLICKED name.
 * All other keys are merged into the tracked payload as extra properties.
 */
export interface AnalyticsOverride {
  event?: string
  [key: string]: unknown
}

interface AutoTrackedClick {
  event: string
  properties: AnalyticsEventProperties
}

export const getAutoTrackedClick = (target: HTMLElement): AutoTrackedClick | null => {
  if (target.closest('[data-track-ignore]')) return null

  const elementWithId = target.closest('[id]') as HTMLElement | null
  if (!elementWithId?.id) return null

  const id = elementWithId.id
  const parts = id.split('-')
  if (parts.length < 3) return null

  const overrideEl = target.closest('[data-analytics]') as HTMLElement | null
  const override: AnalyticsOverride = overrideEl?.dataset?.analytics
    ? (() => {
        try {
          return JSON.parse(overrideEl.dataset.analytics)
        } catch {
          return {}
        }
      })()
    : {}

  const { event, ...extraProps } = override

  return {
    event: event ?? AnalyticsEvents.ELEMENT_CLICKED,
    properties: {
      elementId: id,
      elementType: target.tagName.toLowerCase(),
      elementText: target.textContent?.trim().substring(0, 100) || undefined,
      page: parts[0],
      context: parts.slice(1, -1).join('-'),
      element: parts.at(-1),
      extra: extraProps,
    },
  }
}

/**
 * v-track-ignore
 * Marks an element (and its descendants) as opted-out of auto-click tracking.
 *
 * @example
 * <button v-track-ignore @click="doSomething">Not tracked</button>
 */
export const vTrackIgnore: ObjectDirective<HTMLElement> = {
  mounted(el) {
    el.dataset.trackIgnore = ''
  },
}

/**
 * v-track
 * Overrides the event name and/or injects extra properties into the auto-tracked
 * click payload. The element still needs an id that matches the 3-part convention
 * (page-context-element) for the click listener to fire.
 *
 * @example
 * <!-- Override event name + add a property -->
 * <div id="courses-hero-cta" v-track="{ event: 'hero_cta_clicked', campaign: 'spring' }">
 *
 * <!-- Add a property only (event name stays element_clicked) -->
 * <div id="courses-hero-cta" v-track="{ placement: 'above_fold' }">
 */
export const vTrack: ObjectDirective<HTMLElement, AnalyticsOverride> = {
  mounted(el, binding) {
    if (binding.value) el.dataset.analytics = JSON.stringify(binding.value)
  },
  updated(el, binding) {
    if (binding.value) {
      el.dataset.analytics = JSON.stringify(binding.value)
    } else {
      delete el.dataset.analytics
    }
  },
}
